import { CSSProperties, PropsWithChildren, ReactNode, forwardRef } from 'react';

import type { Placement } from '@floating-ui/core';

import { styled } from '../theme';
import { COMPACT_BUTTON_SIZE } from '../theme/theme-config';
import { Color, Size, matchSize } from '../utility/common-props-helpers';
import Loading from './Loading';
import SvgIcon from './SvgIcon';
import Text from './Text';
import Tooltip from './Tooltip';

/**
 * Types
 */

export type ButtonProps = {
    className?: string;
    disabled?: boolean;
    loading?: boolean;
    style?: CSSProperties;
    type?: 'button' | 'submit' | 'reset';
    color?: Color;
    beforeIconSrc?: string;
    isTextButton?: boolean;
    onClick?: (ev: any) => void;
    size?: Size;
    form?: string;
    tooltip?: ReactNode;
    tooltipPlacement?: Placement;
} & PropsWithChildren;

/**
 * Styled
 */

const BaseButton = styled(Text.withComponent('button'))<ButtonProps>(({ theme, size }) => ({
    minHeight: matchSize({ '2xs': 34, xs: 40, sm: COMPACT_BUTTON_SIZE, md: 50 })(size),
    height: matchSize({ '2xs': 34, xs: 40, sm: COMPACT_BUTTON_SIZE, md: 50 })(size),
    minWidth: 200,
    fontSize: size === '2xs' ? '1.4rem' : '1.6rem',
    padding: size === '2xs' ? '6px 14px' : '6px 20px',
    cursor: 'pointer',
    borderRadius: theme.borderRadius.base,
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    position: 'relative',
}));

const TextButton = styled(BaseButton)<ButtonProps>(({ theme, disabled, color }) => ({
    color: theme.color[`${color}Text`],
    background: theme.color[`${color}1`],
    border: 'none',
    ':hover': {
        color: theme.color[`${color}2`],
    },
    ':active, &.active': {
        color: theme.color[`${color}3`],
    },

    ...(disabled && {
        cursor: 'default',
        pointerEvents: 'none',
        color: theme.color.disabled,
        background: theme.color[`${color}1`],
    }),
}));

const ColoredButton = styled(BaseButton)<ButtonProps>(({ theme, disabled, color }) => ({
    color: theme.color[`${color}Text`],
    background: theme.color[`${color}1`],
    border: theme.border[`${color}`] || 'unset',
    boxShadow: theme.boxShadow[`${color}1`],
    ':hover': {
        background: theme.color[`${color}2`],
        boxShadow: theme.boxShadow[`${color}2`],
    },
    ':active, &.active': {
        background: theme.color[`${color}3`],
        boxShadow: theme.boxShadow[`${color}3`],
    },

    ...(disabled && {
        cursor: 'default',
        pointerEvents: 'none',
        background: theme.color.disabled,
        boxShadow: 'unset',
    }),
}));

const OutlinedButton = styled(BaseButton)<ButtonProps>(({ theme, disabled, color }) => ({
    color: theme.color[`${color}Text`],
    background: theme.color[`${color}1`],
    border: theme.border[`${color}`] || 'unset',
    ':hover': {
        background: theme.color[`${color}2`],
        color: theme.color[`${color}1`],

        svg: {
            color: theme.color.white,
        },
    },
    ':active, &.active': {
        background: theme.color[`${color}3`],
        color: theme.color[`${color}1`],
    },
    ...(disabled && {
        cursor: 'default',
        pointerEvents: 'none',
        background: theme.color.white,
        color: theme.color.disabled,
        borderColor: theme.color.disabled,
    }),
}));

const StyledIcon = styled(SvgIcon)({
    marginRight: 10,
});

/**
 * Component
 */

const Button = forwardRef(
    (
        {
            beforeIconSrc,
            children,
            color = 'base',
            type = 'button',
            isTextButton = false,
            loading = false,
            tooltip,
            tooltipPlacement,
            ...props
        }: ButtonProps,
        ref
    ) => {
        const StyledButton = isTextButton
            ? TextButton
            : color === 'base'
              ? OutlinedButton
              : ColoredButton;

        const button = (
            <StyledButton color={color} ref={ref} type={type} {...props}>
                {loading ? (
                    <Loading size="small" centered />
                ) : (
                    <>
                        {beforeIconSrc && <StyledIcon src={beforeIconSrc} />}
                        {children}
                    </>
                )}
            </StyledButton>
        );

        if (tooltip == null) {
            return button;
        }

        return (
            <Tooltip title={tooltip} placement={tooltipPlacement}>
                {button}
            </Tooltip>
        );
    }
);

export default Button;
